import { createContext, createElement, useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react';

export type SoundName = 'click' | 'advance' | 'success' | 'error' | 'checkin';

interface SoundContextValue {
  enabled: boolean;
  toggle: () => void;
  play: (sound: SoundName) => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);
const STORAGE_KEY = 'forteens.sound-enabled';

function loadPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function SoundProvider({ children }: PropsWithChildren): JSX.Element {
  const [enabled, setEnabled] = useState(loadPreference);

  const toggle = useCallback(() => {
    setEnabled((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Preference is optional and should never break interaction.
      }
      return next;
    });
  }, []);

  const play = useCallback((sound: SoundName) => {
    if (!enabled) return;
    const audio = new Audio(`/assets/sounds/${sound}.wav`);
    audio.volume = sound === 'error' ? 0.18 : 0.12;
    audio.preload = 'auto';
    void audio.play().catch(() => {
      // Intentional silent fallback: browser policy or absent optional asset.
    });
  }, [enabled]);

  const value = useMemo(() => ({ enabled, toggle, play }), [enabled, play, toggle]);
  return createElement(SoundContext.Provider, { value }, children);
}

export function useSound(): SoundContextValue {
  const value = useContext(SoundContext);
  if (!value) throw new Error('useSound must be used inside SoundProvider');
  return value;
}
